import { useMicdropState } from '@micdrop/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface AnimatedAssistantStateProps {
  customStates?: Array<{
    condition: boolean;
    message: string;
  }>;
}

const AnimatedAssistantState = ({ customStates = [] }: AnimatedAssistantStateProps) => {
  const aiState = useAiState(customStates);

  return (
    <div className="flex items-center justify-center min-h-[24px] mx-auto">
      <AnimatePresence mode="wait">
        {aiState && (
          <motion.div
            key={aiState}
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            }}
            className="text-center text-lg text-gray-500"
          >
            {aiState}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function useAiState(customStates: AnimatedAssistantStateProps['customStates']): string | null {
  const { t } = useTranslation();
  const { isListening, isProcessing, isPaused } = useMicdropState();

  const state = useMemo(() => {
    if (customStates) {
      for (const customState of customStates) {
        if (customState.condition) {
          return customState.message;
        }
      }
    }

    if (isProcessing) {
      return t('Interview.state.thinking');
    } else if (isListening) {
      return t('Interview.state.listening');
    } else if (isPaused) {
      return t('Interview.state.paused');
    } else {
      return null;
    }
  }, [isListening, isProcessing, isPaused, customStates, t]);

  return state;
}

export default AnimatedAssistantState;
